import { IconCopy, IconPlus } from "@tabler/icons-react";
import { Fragment } from "react";
import RowActionButton from "src/generator/_Buttons/RowActionButton/RowActionButton";
import MaxRowsWarningModal from "src/generator/_Modals/MaxRowsWarningModal/MaxRowsWarningModal";
import { GeneratorButtonsContainer } from "src/generator/Generator.styled";
import { useCanEditEntry } from "src/generator/hooks/useCanEditEntry";
import { useSelector } from "src/redux/hooks";

import InlineSaveButton from "../../../_Buttons/InlineSaveButton/InlineSaveButton";
import useAddDynamicRow from "../../../hooks/useAddDynamicRow";
import useIsSaveRequired from "../../../hooks/useIsSaveRequired";
import NmeaModal from "./NmeaModal";
import TimeWarning from "./TimeWarning";

interface Props {
  sectionIndex: number;
  addRows: boolean;
  cloneRows?: boolean;
  copyPrevVal?: number;
  inlineSaving?: boolean;
  addToTop?: boolean;
  sectionsClassName: string;
  containerStyle?: React.CSSProperties;
}

export interface WarningModal {
  type: "warn" | "limit" | "security";
  text?: string;
  addNewRowProps?: {
    sectionIndex: number;
    isCopyOn: boolean;
    omitWarn: boolean;
  };
}

// for scrolling correction issue
declare global {
  interface Window {
    scSX: any;
    scSY: any;
    registeredScrollCorrection: any;
    pendingScrollCorrection: any;
  }
}

const AddDynamicRow = ({
  sectionIndex,
  cloneRows,
  copyPrevVal,
  addRows,
  inlineSaving,
  sectionsClassName,
  addToTop,
  containerStyle,
}: Props) => {
  const { sections, catSelect } = useSelector(
    state => state.generatorSlice.lbentry,
  );
  const canEditEntry = useCanEditEntry(sectionIndex);
  const { locked, you } = useSelector(state => state.entrySlice);

  const { addDynamicRow, isWarningModalOpened, setIsWarningModalOpened } =
    useAddDynamicRow({ copyPrevVal });

  const isSaveRequired = useIsSaveRequired(
    sectionsClassName,
    Boolean(inlineSaving),
    sectionIndex,
  );

  const handleYes = (data: WarningModal) => {
    if (isWarningModalOpened?.type !== "warn" || !data.addNewRowProps) return;
    addDynamicRow(
      data.addNewRowProps.sectionIndex,
      data.addNewRowProps.isCopyOn,
      data.addNewRowProps.omitWarn,
    );
  };

  if (!canEditEntry || (locked && !you)) {
    return <TimeWarning sectionIndex={sectionIndex} />;
  }

  return (
    <>
      <TimeWarning sectionIndex={sectionIndex} />
      <GeneratorButtonsContainer style={containerStyle}>
        {inlineSaving && isSaveRequired && <InlineSaveButton />}
        {(!inlineSaving || !isSaveRequired) && addRows && (
          <Fragment>
            <RowActionButton
              icon={<IconPlus size="1.5rem" />}
              onClick={() => {
                addDynamicRow(sectionIndex, false, false, false, addToTop);
              }}>
              {catSelect ? "Add Category" : "Add"}
            </RowActionButton>

            {catSelect && (
              <RowActionButton
                icon={<IconPlus size="1.5rem" />}
                onClick={() =>
                  addDynamicRow(sectionIndex, false, false, true, addToTop)
                }>
                Add Row
              </RowActionButton>
            )}
          </Fragment>
        )}

        {(!inlineSaving || !isSaveRequired) && cloneRows && (
          <RowActionButton
            icon={<IconCopy size="1.5rem" />}
            onClick={() =>
              addDynamicRow(sectionIndex, true, false, false, addToTop)
            }>
            Copy
          </RowActionButton>
        )}
      </GeneratorButtonsContainer>
      <MaxRowsWarningModal
        data={isWarningModalOpened}
        close={() => setIsWarningModalOpened(null)}
        text={
          (isWarningModalOpened?.type === "security"
            ? isWarningModalOpened.text
            : sections[sectionIndex].rowWarningText
              ? sections[sectionIndex].rowWarningText
              : sections[sectionIndex].rowLimitText) ??
          "This entry can no longer be edited as 24 hours have been completed, please select New Entry."
        }
        maxRows={sections[sectionIndex].rowLimit as number}
        onSelectYes={handleYes}
      />
      <NmeaModal sectionIndex={sectionIndex} copyPrevVal={copyPrevVal} />
    </>
  );
};

export default AddDynamicRow;
